import { format } from 'date-fns'
import { fr } from 'date-fns/locale'
import { cycleWeekIndex, parseDateKey } from '../lib/cycle'
import type { CustomChore, ScheduleKind } from '../types'

/**
 * How a chore's recurrence reads in French, in the two places it has to.
 *
 * It lives under `components/` rather than `lib/` because it is user-facing
 * copy: the rule about which schedules a given day can offer is in
 * `scheduleOptions`, and the rule about what a stored schedule *means* is in
 * `buildPlan`. This file only puts words to them.
 */

export interface ScheduleOption {
  kind: ScheduleKind
  label: string
}

/**
 * The recurrences worth offering for one day.
 *
 * Only the ones that would actually put the chore on the day being looked at.
 * The rotation slot exists on any Saturday — the cycle week is read off the
 * date — but "un dimanche par mois" fires on the Sunday of cycle week 3 alone,
 * so offering it on any other Sunday would silently schedule the chore for a
 * day up to three weeks away.
 */
export function scheduleOptions(date: Date): ScheduleOption[] {
  const week = cycleWeekIndex(date)

  const options: ScheduleOption[] = [
    { kind: 'once', label: `Seulement le ${format(date, 'd MMMM', { locale: fr })}` },
    { kind: 'daily', label: 'Tous les jours' },
    { kind: 'weekday', label: `Chaque ${format(date, 'EEEE', { locale: fr })}` },
  ]

  if (date.getDay() === 6) {
    options.push({
      kind: 'saturday-rotation',
      label: `Un samedi sur quatre (semaine ${week + 1})`,
    })
  }

  if (date.getDay() === 0 && week === 3) {
    options.push({ kind: 'monthly-sunday', label: 'Un dimanche par mois' })
  }

  return options
}

const WEEKDAYS = ['dimanche', 'lundi', 'mardi', 'mercredi', 'jeudi', 'vendredi', 'samedi']

/** One line describing when a stored chore comes round, for the settings list. */
export function describeSchedule(chore: CustomChore): string {
  switch (chore.schedule) {
    case 'daily':
      return 'Tous les jours'
    case 'weekday': {
      const name = WEEKDAYS[chore.weekday]
      return name ? `Chaque ${name}` : 'Chaque semaine'
    }
    case 'saturday-rotation':
      return `Un samedi sur quatre (semaine ${chore.cycleWeek + 1})`
    case 'monthly-sunday':
      return 'Un dimanche par mois'
    case 'once': {
      const date = parseDateKey(chore.onDate)
      return date ? `Le ${format(date, 'd MMMM yyyy', { locale: fr })}` : 'Une seule fois'
    }
    default:
      // Written by a newer version of the app; `buildPlan` does not show the
      // chore, so say plainly that it is not scheduled rather than guessing.
      return 'Jamais planifiée'
  }
}
